<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="760px">
    <v-card v-if="selectedInvoice" v-draggable-dialog class="profile-dialog-card entity-form-dialog invoice-payment-dialog">
      <EntityDialogHeader
        class="dialog-drag-handle"
        eyebrow="Invoice payment"
        :title="`Receive payment #${selectedInvoice.localId}`"
        :subtitle="isCustomer ? `Record a payment for ${Customer.name || 'this parent'}.` : 'Record a donor payment.'"
        icon="mdi-cash-check"
        close-label="Close receive payment dialog"
        @close="close"
      />

      <v-divider />

      <v-card-text class="profile-dialog-body entity-form-dialog__body">
        <section v-if="isCustomer" class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-receipt-text-outline</v-icon></span>
            <div><h3>Invoice balance</h3><p>Review what remains before recording this payment.</p></div>
          </div>
          <div class="invoice-payment-dialog__balances">
            <article><small>Masjid due</small><strong>{{ donationPay - totalDonationCollected | currency }}</strong><span>{{ totalDonationCollected | currency }} paid</span></article>
            <article><small>Parent due</small><strong>{{ parentDueBalance | currency }}</strong><span>{{ totalParentPaymentCollected | currency }} paid</span></article>
            <article class="invoice-payment-dialog__balance--total"><small>Invoice due</small><strong>{{ totalDueBalance | currency }}</strong><span>Total remaining</span></article>
          </div>
        </section>

        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-swap-horizontal-bold</v-icon></span>
            <div><h3>How is this being paid?</h3><p>Use prepaid tuition, record money received, or collect from a saved card.</p></div>
          </div>
          <v-btn-toggle v-model="paymentSource" mandatory class="invoice-payment-dialog__source" color="primary">
            <v-btn value="manual"><v-icon left small>mdi-cash</v-icon>Manual</v-btn>
            <v-btn value="online"><v-icon left small>mdi-credit-card-outline</v-icon>Saved card</v-btn>
            <v-btn v-if="isCustomer" value="advance" :disabled="advanceSummary.available <= 0"><v-icon left small>mdi-wallet-outline</v-icon>Advance</v-btn>
          </v-btn-toggle>
          <div v-if="isCustomer" class="entity-form-dialog__summary invoice-payment-dialog__advance-summary">
            <v-icon size="18" :color="advanceSummary.available > 0 ? 'primary' : 'grey'">mdi-wallet-outline</v-icon>
            <span><strong>{{ advanceSummary.available | currency }}</strong> advance tuition available<span v-if="paymentSource === 'advance'"> · {{ advanceApplicationAmount | currency }} will be applied to this invoice</span></span>
          </div>
        </section>

        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-form-textbox</v-icon></span>
            <div><h3>Payment details</h3><p v-if="paymentSource === 'advance'">This creates a traceable invoice allocation and reduces the advance balance.</p><p v-else>Enter the amount and supporting payment information.</p></div>
          </div>
          <v-row>
            <v-col v-if="paymentSource === 'online'" cols="12" sm="6">
              <v-select v-model="selectedMerchant" :items="MerchantAccount" return-object item-text="name" outlined dense hide-details label="Merchant" />
            </v-col>
            <v-col v-if="paymentSource === 'online'" cols="12" sm="6">
              <v-select v-model="selectedCard" :items="cards" :loading="loadingCard" item-text="last4" return-object outlined dense hide-details label="Saved card">
                <template v-slot:item="{ item }"><span>•••• {{ item.last4 }}</span><v-spacer /><span class="text-capitalize">{{ item.payment_method_type }}</span></template>
              </v-select>
            </v-col>
            <v-col v-if="paymentSource !== 'advance'" cols="12" sm="6">
              <DateSelector :initValue="Payment.date" label="Payment date" v-model="Payment.date" />
            </v-col>
            <v-col v-if="paymentSource === 'manual'" cols="12" sm="6">
              <v-select v-model="Payment.paymentMethod" :items="paymentMethods" outlined dense hide-details label="Payment method" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.number="Payment.amount" class="amount-input" type="number" min="0.01" :max="maximumPayment" outlined dense hide-details prefix="$" :label="isCustomer ? 'Parent payment amount' : 'Donation payment amount'" />
            </v-col>
            <v-col v-if="paymentSource === 'manual'" cols="12" sm="6">
              <v-text-field v-model="Payment.referenceNumber" outlined dense hide-details label="Reference number" />
            </v-col>
            <v-col v-if="paymentSource !== 'advance'" cols="12">
              <v-text-field v-model="Payment.note" outlined dense hide-details label="Memo (optional)" />
            </v-col>
          </v-row>
        </section>

        <section v-if="isCustomer && paymentSource !== 'advance' && donationPay - totalDonationCollected > 0" class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-hand-heart-outline</v-icon></span>
            <div><h3>Permanent Sadaqah support</h3><p>Record the remaining Masjid responsibility as permanent support on the selected payment date.</p></div>
          </div>
          <v-switch v-model="Payment.donation" inset hide-details label="Include permanent Masjid support" />
          <v-row v-if="Payment.donation" class="mt-2">
            <v-col cols="12" sm="6"><v-select v-model="Payment.donationPaymentMethod" :items="paymentMethods" outlined dense hide-details label="Payment method" /></v-col>
            <v-col cols="12" sm="6"><v-text-field v-model.number="Payment.donationAmount" class="amount-input" type="number" outlined dense hide-details prefix="$" label="Amount" /></v-col>
            <v-col cols="12"><v-text-field v-model="Payment.donationReferenceNumber" outlined dense hide-details label="Reference number" /></v-col>
          </v-row>
        </section>
      </v-card-text>

      <v-card-actions class="profile-dialog-actions entity-form-dialog__actions">
        <div class="entity-form-dialog__status"><span>{{ paymentSource === 'advance' ? 'Advance applied' : 'Payment total' }}</span><strong>{{ totalCollected() | currency }}</strong></div>
        <v-spacer />
        <v-btn text :disabled="loadingBtn" @click="close">Cancel</v-btn>
        <v-btn color="primary" depressed :disabled="submitStatus()" :loading="loadingBtn" @click="submit">
          <v-icon left small>{{ paymentSource === 'advance' ? 'mdi-wallet-check-outline' : 'mdi-content-save-outline' }}</v-icon>
          {{ paymentSource === 'advance' ? 'Apply advance' : 'Receive payment' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import invoiceService from "@/modules/Invoice/service.js";
import DateSelector from "@/components/DateSelector";
import payCompassService from "@/modules/PayCompass/service.js";
import paymentService from "@/modules/Payment/service.js";
import donationCategoryService from "@/modules/DonationCategory/service.js";
import donorService from "@/modules/Donors/service.js";
import customerService from "@/modules/Customer/service.js";
import advancePaymentService from "@/modules/AdvancePayment/service.js";
import paymentValidation from "@/modules/Invoice/Payment/paymentValidation.js";

export default {
  name: 'payment-receive-from-payment',
  props: ['Invoice', 'openReceivePaymentDialog'],
  data() {
    return {
      Customer: null,
      selectedMerchant: null,
      MerchantAccount: [],
      isCustomer: false,
      collectResult: null,
      collectedOnline: false,
      paymentSource: 'manual',
      advanceSummary: { received: 0, applied: 0, available: 0 },
      dialog: false,
      loadingCard: false,
      selectedInvoiceParentPayDue: 0,
      selectedInvoice: null,
      paymentType: 'manual',
      loadingBtn: false,
      selectedCard: null,
      Payment: {
        CustomerId: 0,
        date: this.$moment().toISOString(),
        referenceNumber: null,
        donationReferenceNumber: null,
        paymentMethod: "Online",
        donationPaymentMethod: "Masjid",
        PaymentDetails: [],
        amount: 0,
        donationAmount: 0,
        donation: false,
        parent: false,
        note: null,
      },
      paymentMethods: [
        'Stripe',
        'Online',
        'Mohid',
        "Cash",
        "Check",
        "Paypal",
        "Debit Card",
        "Credit Card",
        "Wire",
        "Quick Pay",
        "Cash App",
        "Exchange",
        "Masjid",
        "Zelle",
        "Other",
      ],
      cards: [],
      dueAmount: 0,
      parentPay: 0,
      donationPay: 0,
      totalDonationCollected: 0,
      totalParentPaymentCollected: 0,
    }
  },
  computed: {
    isParentOnlyInvoice() {
      return Boolean(this.selectedInvoice && this.selectedInvoice.metadata &&
        (this.selectedInvoice.metadata.source === 'departmentAnnualCharge' ||
          this.selectedInvoice.metadata.responsibility === 'parentOnly'))
    },
    totalDueBalance() {
      return (this.donationPay + this.parentPay) - (this.totalParentPaymentCollected + this.totalDonationCollected)
    },
    parentDueBalance() {
      return Math.max(0, this.parentPay - this.totalParentPaymentCollected)
    },
    maximumPayment() {
      return paymentValidation.maximumInvoicePayment({
        isCustomer: this.isCustomer,
        paymentSource: this.paymentSource,
        parentDueBalance: this.parentDueBalance,
        totalDueBalance: this.totalDueBalance,
        advanceAvailable: this.advanceSummary.available,
      })
    },
    advanceApplicationAmount() {
      if (this.paymentSource !== 'advance') return 0
      return Math.min(Math.max(0, Number(this.Payment.amount || 0)), this.maximumPayment)
    }
  },
  watch: {
    selectedMerchant(val) {
      // console.log('val', val)
      // console.log('customer', this.getCustomer)
      if (val && this.paymentSource === 'online') {
        // this.cards = []
        this.getCard()
      }

    },
    paymentSource(val) {
      this.collectedOnline = val === 'online'
      this.Payment.donation = false
      if (val === 'advance') {
        this.Payment.amount = this.maximumPayment
        this.Payment.paymentMethod = 'Advance tuition credit'
      } else {
        this.Payment.amount = this.maximumPayment
        if (val === 'manual' && this.Payment.paymentMethod === 'Advance tuition credit') this.Payment.paymentMethod = 'Cash'
        if (val === 'online' && this.selectedMerchant) this.getCard()
      }
    },
    async collectedOnline(val) {
      if (val) {
        //console.log('this.selectedInvoice.Customer', this.selectedInvoice.Customer)
        if (this.isCustomer) {
          this.getCard(this.selectedInvoice.Customer)
        } else {
          this.getCard(this.selectedInvoice.Donor)
        }
        this.Payment.date = this.$moment().toISOString()
      }
    },
    openReceivePaymentDialog(val) {
      this.dialog = val
    },
    async dialog(val) {
      if (val) {
        console.log('calling invoice', this.Invoice)
        await this.getCustomer()
        await Promise.all([this.merchantServiceList(), this.getAdvanceSummary()])
        await this.getInvoice()
      }
    }
  },
  components: {
    DateSelector,
  },
  methods: {
    async getCustomer() {
      if (this.Invoice.DonorId) {
        this.Customer = (await donorService.getById(this.Invoice.DonorId)).data
      } else {
        this.Customer = (await customerService.getById(this.Invoice.CustomerId)).data
      }

      console.log('Customer', this.Customer)
    },
    merchantServiceList() {
      return donationCategoryService.getAll().then((response) => {
        // this.loading = false;
        //console.log('response merchant', response)
        this.MerchantAccount = response.data;
        this.selectedMerchant = this.MerchantAccount[1]
        // this.itemsPerPage = this.merchants.length
      });
    },
    getAdvanceSummary() {
      if (!this.Invoice.CustomerId) return Promise.resolve()
      return advancePaymentService.getSummary(this.Invoice.CustomerId).then(response => {
        this.advanceSummary = response.data || { received: 0, applied: 0, available: 0 }
        return response
      })
    },
    submitStatus() {
      return paymentValidation.isInvoicePaymentDisabled({
        total: this.totalCollected(),
        totalDueBalance: this.totalDueBalance,
        paymentAmount: this.Payment.amount,
        maximumPayment: this.maximumPayment,
        paymentSource: this.paymentSource,
        advanceApplicationAmount: this.advanceApplicationAmount,
        hasMerchant: Boolean(this.selectedMerchant),
        hasCard: Boolean(this.selectedCard),
      })
    },
    totalCollected() {
      if (this.paymentSource === 'advance') return this.advanceApplicationAmount
      let total = 0
      if (this.Payment.parent) {
        total += parseFloat(this.Payment.amount)
      }
      if (this.Payment.donation) {
        total += parseFloat(this.Payment.donationAmount)
      }
      return total
    },
    getCard() {
      this.cards = []
      this.payCompassId = null
      this.Customer.MerchantCustomers.map(row => {
        if (row.DonationCategoryId === this.selectedMerchant.id) {
          this.payCompassId = row.payCompassId
        }
      })
      console.log('payCompassId', this.payCompassId)
      payCompassService.getAllCard(this.payCompassId, { donationCategoryId: this.selectedMerchant.id }).then(response => {
        //console.log('card list', response)
        this.cards = response.data
        if (this.cards.length === 1) {
          this.selectedCard = this.cards[0]
        }
      })
    },
    async submit() {
      if (this.paymentSource === 'advance') return this.applyAdvancePayment()
      // console.log('in here', this.collectedOnline, this.selectedCard)
      if (this.collectedOnline && !this.selectedCard) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Select card on file",
          });
        return false
      }

      this.loadingBtn = true
      if (this.isCustomer) {
        this.Payment.CustomerId = this.selectedInvoice.CustomerId
      } else {
        this.Payment.DonorId = this.selectedInvoice.DonorId
      }

      if (this.Payment.parent && this.Payment.amount !== 0) {
        let payment = {
          amount: this.Payment.amount,
          referenceNumber: this.Payment.referenceNumber,
          InvoiceId: this.selectedInvoice.id,
          donation: false
        }
        if (this.collectedOnline) {
          payment.donationCategoryId = this.selectedMerchant.id
        }
        console.log(';alksdjfa;lskdfj', payment)
        this.Payment.PaymentDetails.push(payment)
      }
      if (this.Payment.donation && this.Payment.donationAmount !== 0) {
        this.Payment.PaymentDetails.push({
          amount: this.Payment.donationAmount,
          referenceNumber: this.Payment.donationReferenceNumber,
          InvoiceId: this.selectedInvoice.id,
          donation: this.Payment.donation,
          metadata: { source: 'PERMANENT_SADAQAH_ASSISTANCE' }
        })
      }
      // console.log('this.Payment', this.Payment)
      if (this.collectedOnline) {
        this.collectResult = await this.collectPayment()
        // console.log('this.collectResult', this.collectResult)
        if (this.collectResult) {
          this.savePayment()
        }
        this.$events.emit("invoiceListEvent");
      } else {
        this.savePayment()
      }

      // console.log('collectResult', collectResult)
      // this.savePayment()
      // if (this.paymentType === 'online') {
      //   this.collectPayment()
      // } else {
      //   this.savePayment()
      // }
    },
    async applyAdvancePayment() {
      this.loadingBtn = true
      try {
        const response = await advancePaymentService.applyToInvoice({
          invoiceId: this.selectedInvoice.id,
          maxAmount: Number(this.totalParentPaymentCollected) + this.advanceApplicationAmount,
        })
        const result = response.data || {}
        if (!(Number(result.applied) > 0)) throw new Error('No advance tuition balance was applied')
        this.$events.emit("invoiceListEvent")
        this.$events.emit("customerProfileEvent")
        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 }).fire({
          icon: "success",
          title: `${this.$options.filters.currency(Number(result.applied))} applied from advance tuition`,
        })
        this.close()
      } catch (error) {
        this.loadingBtn = false
        this.$swal({
          icon: "error",
          title: "Advance payment was not applied",
          text: (error.data && error.data.message) || error.message || "Please try again.",
        })
      }
    },
    async collectPayment() {
      try {
        let payment = {
          amount: this.Payment.amount,
          description: `Invoice #${this.selectedInvoice.localId}, IntId: ${this.selectedInvoice.id}`,
          source: this.selectedCard.id,
          customer: (this.isCustomer ? this.selectedInvoice.Customer.payCompassId : this.selectedInvoice.Donor.payCompassId),
          cardId: this.selectedCard.id,
          selectedInvoice: this.selectedInvoice,
          donationCategoryId: this.selectedMerchant.id
        }
        console.log('payment', payment)
        const paymentResponse = await payCompassService.chargeCustomer(payment)
        console.log('response payment', paymentResponse)
        if (paymentResponse && paymentResponse.data && paymentResponse.data.status_code === 'A') {
          this.Payment.referenceNumber = `${this.selectedCard.payment_method_type}-${this.selectedCard.last4}`
          this.Payment.PaymentDetails[0].stripeChargeId = paymentResponse.data.reference_number
          this.Payment.donation = false
          this.Payment.parent = true
          this.Payment.PaymentDetails[0].metadata = paymentResponse.data
          return paymentResponse
        } else {
          this.$swal(paymentResponse.data.status, paymentResponse.data.error_message, 'error')
          this.loadingBtn = false
          return false
        }
      } catch (error) {
        console.log('error', error)
        this.loadingBtn = false
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: error.data.message,
          });
        return false
      }


    },
    savePayment() {
      return paymentService.create(this.Payment).then(() => {

        this.$events.emit("invoiceListEvent");
        this.$events.emit("customerProfileEvent");
        this.loadingBtn = false
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: "Payment was created",
          });
        this.close()
      }).catch((err) => {
        this.loading = false;
        this.loadingBtn = false
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: err.data.message,
          });
      });
    },
    reset() {
      this.collectedOnline = false
      this.paymentSource = 'manual'
      this.advanceSummary = { received: 0, applied: 0, available: 0 }
      this.selectedCard = null
      this.loadingCard = false
      this.selectedInvoiceParentPayDue = 0
      this.selectedInvoice = null
      this.cards = []
      this.dueAmount = 0
      this.parentPay = 0
      this.donationPay = 0
      this.paymentType = 'manual'
      this.totalDonationCollected = 0
      this.loadingBtn = false
      this.totalParentPaymentCollected = 0
      this.Payment = {
        CustomerId: 0,
        date: this.$moment().toISOString(),
        referenceNumber: null,
        donationReferenceNumber: null,
        paymentMethod: "Online",
        donationPaymentMethod: "Masjid",
        PaymentDetails: [],
        amount: 0,
        donationAmount: 0,
        donation: false,
        parent: true,
      }
    },
    close() {
      this.reset()
      this.$emit('close')
      // this.paymentDialog = false
    },
    async getInvoice() {
      this.selectedInvoice = (await invoiceService.getById(this.Invoice.id)).data
      // console.log('this.selectedInvoice', this.selectedInvoice)

      this.selectedInvoice.InvoiceDetails.map(row => {
        // console.log('row', row)
        this.dueAmount += (row.price * row.qty)
        if (this.isParentOnlyInvoice) {
          this.parentPay += (row.price * row.qty)
        } else {
          this.parentPay += (row.parentPay * row.qty)
          this.donationPay += (row.price * row.qty) - (row.parentPay * row.qty)
        }
      })


      // console.log('parentPay', this.dueAmount, this.parentPay, this.donationPay)

      this.selectedInvoice.PaymentDetails.map(row => {
        // console.log('Receive Payment #', row)
        if (row.donation || row.PayrollTeacherId || row.InvoicePayerAllocationId) {
          this.totalDonationCollected += row.amount
        } else {
          this.totalParentPaymentCollected += row.amount
        }
      })

      if (this.selectedInvoice.Customer) {
        this.isCustomer = true
        this.Payment.amount = this.parentPay - this.totalParentPaymentCollected
        this.Payment.donationAmount = this.donationPay - this.totalDonationCollected

        this.Payment.date = this.$moment().toISOString()
      } else {
        this.Payment.parent = true
        this.paymentSource = 'online'
        this.Payment.amount = this.selectedInvoice.due
        this.isCustomer = false
      }

      if (this.parentPay > 0) {
        this.Payment.parent = true
      }
      console.log('this.donationPay', this.donationPay)
      if (this.isCustomer) {
        if (this.donationPay > 0) {
          this.Payment.donation = true
        }
      }



    }
  },
}
</script>
<style scoped>
.invoice-payment-dialog__balances {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.invoice-payment-dialog__balances article {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 15px;
  border: 1px solid #e0e7e1;
  border-radius: 10px;
  background: #fff;
}
.invoice-payment-dialog__balances small,
.invoice-payment-dialog__balances span {
  color: #738078;
}
.invoice-payment-dialog__balances strong {
  color: #24372a;
  font-size: 20px;
}
.invoice-payment-dialog__balance--total {
  background: #f1f8f2 !important;
  border-color: #cfe2d1 !important;
}
.invoice-payment-dialog__source {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: 100%;
}
.invoice-payment-dialog__source .v-btn {
  min-width: 0 !important;
  height: 48px;
  text-transform: none;
  letter-spacing: 0;
}
.invoice-payment-dialog__advance-summary {
  margin-top: 12px;
}
@media (max-width: 600px) {
  .invoice-payment-dialog__balances,
  .invoice-payment-dialog__source {
    grid-template-columns: 1fr;
  }
}
</style>
